import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { api, fmtErr } from "../../lib/api";
import { PageHeader } from "./Dashboard";
import { EmptyYacht, resizeImageToDataUrl } from "./Inventory";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Plus, Wrench, AlertTriangle, Clock, Camera } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useRights } from "../../lib/useRights";
import { useTour } from "../../components/tour/TourContext";
import TourReplayButton from "../../components/tour/TourReplayButton";
import { useAuth } from "../../context/AuthContext";
import { resolveUnitLocation } from "../../lib/engineLocations";
import FileUploader from "../../components/FileUploader";
import { SYSTEMS, STATUS, STATUS_GROUP_ORDER,
  ENGINE_LIKE_SYSTEMS, DEFAULT, ROUTINE_DEFAULT,
} from "./maintenance/constants";
import { ModalTab } from "./maintenance/components";
import { RoutineTab } from "./maintenance/RoutineTab";
import { TaskGroup } from "./maintenance/TaskGroup";
import { JobDetailModal } from "./maintenance/JobDetailModal";
import { TaskEditorBody } from "./maintenance/TaskEditorBody";

// Re-exported so existing importers (e.g. SupplierTasks) keep working after
// JobDetailModal moved into ./maintenance/JobDetailModal.jsx.
export { JobDetailModal };

// "provider" is the internal role name for online suppliers — always show
// "Supplier" in the UI.

// Restricted variants ("jobs" / "routine") hide the other tab so the
// /tasks and /maintenance routes can each render only their half of the
// Maintenance UX without code duplication. Defaults stay backwards-
// compatible — no caller change required.
export default function Maintenance({
  restrictTo = null,
  pageTitle,
  buttonLabel,
  dialogTitle,
  saveLabel,
  addButtonTestid,
  deleteLabel,
} = {}) {
  const { t } = useTranslation();
  const { activeYacht } = useYacht();
  const { can, role: meRole } = useRights();
  const { activeTour } = useTour();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT);
  // When set, the editor is in "edit existing job" mode → save() PATCHes
  // ``/maintenance/{editingJob.id}`` instead of POSTing a new row.
  const [editingJob, setEditingJob] = useState(null);
  // ``saving`` guards the Save button: without it, an impatient tap on a
  // slow network can fire the POST twice and create duplicate jobs (the
  // user-reported bug from production). It also lets us disable Cancel
  // mid-flight so we don't get half-saved state.
  const [saving, setSaving] = useState(false);
  // When the user picks "Find supplier" + selects suppliers, saving opens a
  // prompt asking whether to send the quote requests now or later.
  const [quotePrompt, setQuotePrompt] = useState(false);
  // Filters are remembered across visits like the scope tab. Deep-link
  // pseudo-statuses (open/open_only/awaiting_confirmation) are session-only.
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem("maint_filter_status") || "");
  // Internal (crew) vs external (supplier) scope tabs on the Tasks list.
  // Last choice is remembered across visits (localStorage).
  const [scopeFilter, setScopeFilter] = useState(() => {
    const v = localStorage.getItem("maint_scope_tab");
    return v === "internal" || v === "external" ? v : "all";
  });
  const pickScope = (v) => {
    setScopeFilter(v);
    localStorage.setItem("maint_scope_tab", v);
  };
  const [categoryFilter, setCategoryFilter] = useState(() => localStorage.getItem("maint_filter_category") || "");
  // Quick "Awaiting quotes" toggle — shows only tasks with an active (not yet
  // accepted) supplier-quote process. ANDs with the status filter.
  const [awaitingQuotes, setAwaitingQuotes] = useState(() => localStorage.getItem("maint_filter_awaiting_quotes") === "1");
  useEffect(() => {
    const pseudo = statusFilter === "open" || statusFilter === "open_only" || statusFilter === "awaiting_confirmation";
    localStorage.setItem("maint_filter_status", pseudo ? "" : statusFilter);
    localStorage.setItem("maint_filter_category", categoryFilter);
    localStorage.setItem("maint_filter_awaiting_quotes", awaitingQuotes ? "1" : "0");
  }, [statusFilter, categoryFilter, awaitingQuotes]);
  const initialView = restrictTo === "routine" ? "routine" : "jobs";
  const [view, setView] = useState(initialView); // 'jobs' | 'routine'
  const [openJob, setOpenJob] = useState(null);
  const [openJobTab, setOpenJobTab] = useState("details");

  // Orphan-upload tracking for the create/edit form. "Before media" is
  // uploaded to R2 immediately on selection but only persisted onto the job
  // when the form is saved. To avoid abandoned files filling storage we track:
  //   • beforeSession  — paths uploaded during THIS form session
  //   • beforeOriginal — paths already saved on the job when the form opened
  // On save we delete (beforeOriginal ∪ beforeSession) − finalSaved (covers
  // uploaded-then-removed AND previously-saved-then-removed). On cancel/close
  // without saving we delete the whole session (none were persisted).
  const beforeSession = useRef(new Set());
  const beforeOriginal = useRef([]);

  // "AI Draft" flow: photograph the problem → AI prefills title, description,
  // system & priority; the photo itself is attached as a before photo.
  const aiDraftRef = useRef(null);
  const handleAiDraft = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let dataUrl = "";
    try {
      dataUrl = await resizeImageToDataUrl(file, 1400);
    } catch {
      toast.error("Could not read that photo — try another one");
      return;
    }
    toast.loading("Drafting task with AI… this takes a few seconds", { id: "ai-draft", duration: Infinity });
    try {
      const [{ data: draft }, photoPath] = await Promise.all([
        api.post("/maintenance/ai-draft", { image_base64: dataUrl }),
        (async () => {
          try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("yacht_id", activeYacht.id);
            fd.append("folder", "maintenance/before");
            const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            return data.storage_path;
          } catch { return null; }
        })(),
      ]);
      setEditingJob(null);
      beforeOriginal.current = [];
      beforeSession.current = new Set(photoPath ? [photoPath] : []);
      setForm({
        ...DEFAULT,
        title: draft.title || "",
        description: draft.description || "",
        system_categories: draft.system_categories || [],
        system_category: (draft.system_categories || [])[0] || "",
        priority: draft.priority || "medium",
        before_photos: photoPath ? [photoPath] : [],
      });
      setOpen(true);
      if (draft.title) {
        toast.success(`Draft ready: ${draft.title} — review & save`, { id: "ai-draft", duration: 8000 });
      } else {
        toast.warning("Couldn't draft a task from that photo — fill the details manually", { id: "ai-draft", duration: 8000 });
      }
    } catch (err) {
      toast.error(fmtErr(err), { id: "ai-draft", duration: 8000 });
    }
  };
  const purgeOrphans = (paths) => {
    for (const p of paths) {
      api.delete("/files/by-path", { data: { path: p } }).catch(() => {});
    }
  };

  // UI strings — `restrictTo` flips them from "Job" wording to "Task" wording
  // so the Tasks route reads naturally without forcing each consumer to pass
  // every label explicitly.
  const TITLE = pageTitle || (restrictTo === "routine" ? t("periodic.title") : t("nav.tasks"));
  const ADD_LABEL = buttonLabel || t("tasks.new_task");
  const DIALOG_TITLE = dialogTitle || t("tasks.new_task");
  const SAVE_LABEL = saveLabel || t("common.save");
  const ADD_TESTID = addButtonTestid || "add-maint-btn";
  // Delete button label inside the JobDetailModal — defaults to "Delete job",
  // but the Tasks page passes "Delete task" so the wording matches the
  // entity being deleted.
  const DELETE_LABEL = deleteLabel || (restrictTo === "routine" ? "Delete periodic task" : "Delete job");

  const load = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/maintenance?yacht_id=${activeYacht.id}`);
      setItems(data);
    } catch {
      setItems([]);
    }
  };
  // Crew members are needed to populate the "Assign to" dropdown — every confirmed
  // member of the yacht (incl. service providers) is eligible.
  const loadMembers = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/yachts/${activeYacht.id}/members`);
      setMembers(data.filter((m) => m.confirmed !== false));
    } catch {
      setMembers([]);
    }
  };
  const loadInventory = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/inventory?yacht_id=${activeYacht.id}`);
      setInventory(data);
    } catch {
      setInventory([]);
    }
  };
  // Suppliers feed the "External supplier" assignee mode. Mirrors Invoices.jsx:
  // merge yacht-scoped + network-wide so the picker shows every supplier the
  // caller can reach. Lower roles (engineer/deckhand) won't have access to the
  // network-wide endpoint — that's fine, we silently fall back to the
  // yacht-scoped list.
  const loadSuppliers = async () => {
    if (!activeYacht?.id) return;
    try {
      const [y, n] = await Promise.all([
        api.get(`/suppliers?yacht_id=${activeYacht.id}`).catch(() => ({ data: [] })),
        api.get(`/suppliers`).catch(() => ({ data: [] })),
      ]);
      const merged = new Map();
      [...(y.data || []), ...(n.data || [])].forEach((s) => {
        if (s && s.id && !merged.has(s.id)) merged.set(s.id, s);
      });
      setSuppliers([...merged.values()]);
    } catch {
      setSuppliers([]);
    }
  };
  useEffect(() => {
    setItems([]); setOpenJob(null); load(); loadMembers(); loadInventory(); loadSuppliers();
    /* eslint-disable-next-line */
  }, [activeYacht?.id]);

  // Allow dashboard widgets to deep-link into a specific job's review modal
  // via ?open=<mnt_id>. Once consumed we strip the query so a reload doesn't
  // reopen the modal unexpectedly. ?status=<value> pre-applies the status
  // filter (used by the dashboard's "Open maintenance" card).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const s = searchParams.get("status");
    if (s) {
      setStatusFilter(s);
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || items.length === 0) return;
    const job = items.find((it) => it.id === openId);
    if (job) {
      setOpenJob(job);
      setOpenJobTab("details");
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  // Mirrors Invoices.jsx::materializeSupplier — when the user picked
  // "+ Add new supplier" AND chose "Yes — save to network", we create
  // the supplier row first and assign it to the active yacht so it shows
  // up in next time's picker. Returns the resolved {supplier_id, name}.
  const materializeExternalSupplier = async (f) => {
    let id = f.external_supplier_id || "";
    let name = (f.external_supplier_name || "").trim();
    if (f._new_supplier && f._new_supplier_save === true) {
      const body = {
        type: f._new_supplier_type || "company",
        name,
        contact_person: f._new_supplier_contact_person || "",
        email: (f._new_supplier_email || "").trim(),
        phone: (f._new_supplier_phone || "").trim(),
        category: f._new_supplier_category || "other",
        notes: "",
      };
      const { data: sup } = await api.post("/suppliers", body);
      try {
        await api.post(`/suppliers/${sup.id}/assign`, { yacht_id: activeYacht.id });
      } catch { /* non-fatal */ }
      id = sup.id;
      name = sup.name;
    }
    return { id: id || null, name };
  };

  const save = async (opts = {}) => {
    // In-flight guard — prevents the "tapped Save twice → two jobs"
    // duplicate-create bug. Any further clicks while ``saving`` is true
    // become no-ops, and the button is visually disabled below.
    if (saving) return;
    setSaving(true);
    try {
      // Block save if any part exceeds available stock — better than letting
      // inventory go negative on the backend.
      for (const p of form.parts) {
        const inv = inventory.find((i) => i.id === p.item_id);
        if (!inv) { toast.error("Pick an inventory item for each part row."); return; }
        if (p.qty <= 0) { toast.error(`Quantity for ${inv.name} must be > 0.`); return; }
        if (p.qty > (inv.quantity || 0)) { toast.error(`Only ${inv.quantity} ${inv.name} in stock.`); return; }
      }
      // External supplier validation. Two paths:
      //  • Online supplier (provider crew) — recorded via assigned_to (they
      //    hold an account). No external_supplier row, no name validation.
      //  • Offline supplier — recorded via external_supplier {id, name}.
      let external_supplier = null;
      let assignee_id = form.assigned_to || null;
      // "My suppliers" on CREATE runs the quote flow (like find_supplier) —
      // legacy external handling only applies when EDITING an old job.
      if (form.assignee_type === "external" && editingJob) {
        if (form.assigned_to) {
          // Online supplier picked — assign to the provider user directly.
          external_supplier = null;
          assignee_id = form.assigned_to;
        } else {
          if (!(form.external_supplier_name || "").trim()) {
            toast.error("Pick a supplier or type a name.");
            return;
          }
          if (form._new_supplier && form._new_supplier_save === null) {
            toast.error("Choose whether to save this supplier to your network.");
            return;
          }
          if (form._new_supplier && form._new_supplier_save === true) {
            if (!(form._new_supplier_email || "").trim() || !(form._new_supplier_phone || "").trim()) {
              toast.error("Email and phone are required to save a supplier to your network.");
              return;
            }
          }
          const sup = await materializeExternalSupplier(form);
          external_supplier = { supplier_id: sup.id, supplier_name: sup.name };
          assignee_id = null;
        }
      } else if (editingJob) {
        // Editing back to a crew assignee → clear any prior external_supplier.
        external_supplier = null;
      }
      // Default due date for supplier (external/find-supplier) tasks on
      // creation: +30 days from today when none is set. Internal crew tasks
      // are left untouched (no auto due date).
      const isVendorSetup = !editingJob && (form.assignee_type === "external" || form.assignee_type === "find_supplier");
      const due_date = (isVendorSetup && !form.due_date)
        ? new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)
        : form.due_date;
      const payload = {
        ...form,
        yacht_id: activeYacht.id,
        due_date,
        cost: form.cost === "" ? null : Number(form.cost),
        assigned_to: assignee_id,
        external_supplier,
        parts_used: form.parts.map((p) => ({
          item_id: p.item_id, item_name: p.item_name, qty: Number(p.qty),
        })),
        after_photos: editingJob ? (editingJob.after_photos || []) : [],
      };
      let savedJob = null;
      if (editingJob) {
        await api.patch(`/maintenance/${editingJob.id}`, payload);
      } else {
        const { data } = await api.post("/maintenance", payload);
        savedJob = data;
      }
      // "Find supplier" mode → optionally fire quote requests to the selected
      // suppliers on the freshly-created task.
      const quoteSel = form._quote_suppliers || [];
      if (savedJob?.id && ["find_supplier", "external"].includes(form.assignee_type) && opts.sendQuotes && quoteSel.length) {
        try {
          const invites = quoteSel.map((s) => ({ supplier_kind: s.kind, supplier_id: s.id }));
          await api.post(`/maintenance/${savedJob.id}/quotes`, { invites, track_offline: !!opts.trackOffline });
          const nOffline = quoteSel.filter((s) => s.mode !== "online").length;
          const nOnline = quoteSel.length - nOffline;
          toast.success(opts.trackOffline && nOffline
            ? `Task saved — ${nOffline} offline supplier(s) tracked offline (no notifications)`
              + (nOnline ? `, quote request sent to ${nOnline} online supplier(s)` : "")
            : `Quote request sent to ${invites.length} supplier(s)`);
        } catch (e) {
          toast.error(`Task created, but sending quotes failed: ${fmtErr(e)}`);
        }
      }
      // Hard-delete any "before media" that was uploaded this session but
      // isn't on the saved job, plus previously-saved items the user removed.
      const finalBefore = form.before_photos || [];
      const orphans = [...new Set([...beforeOriginal.current, ...beforeSession.current])]
        .filter((p) => !finalBefore.includes(p));
      beforeSession.current = new Set();
      beforeOriginal.current = [];
      if (orphans.length) purgeOrphans(orphans);

      // Close the dialog + reset BEFORE awaiting the list refetch so the
      // UI feels instant. ``load()``/`loadInventory()` keep running in
      // the background.
      toast.success(editingJob ? "Job updated" : "Job created");
      setForm(DEFAULT);
      setEditingJob(null);
      setOpen(false);
      load();
      loadInventory();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  // Prefill the editor form from a stored job. Translates the persisted
  // shape back into the editor's UI state — most notably the assignee
  // toggle (crew vs external) and the parts array (called ``parts_used``
  // on the wire but ``parts`` in the editor).
  const startEditJob = (job) => {
    const ext = job.external_supplier || {};
    // An online supplier (provider crew) is stored on assigned_to. Open such
    // jobs in the External-supplier mode so the picker shows them under the
    // "Online suppliers" group (they no longer appear in the crew dropdown).
    const providerIds = new Set(
      members.filter((m) => m.role === "provider").map((m) => m.user_id || m.id),
    );
    const assignedToProvider = !!job.assigned_to && providerIds.has(job.assigned_to);
    setEditingJob(job);
    beforeOriginal.current = job.before_photos || [];
    beforeSession.current = new Set();
    setForm({
      ...DEFAULT,
      title: job.title || "",
      system_category: job.system_category || DEFAULT.system_category,
      system_categories: (job.system_categories && job.system_categories.length)
        ? job.system_categories
        : (job.system_category ? [job.system_category] : []),
      priority: job.priority || "medium",
      status: job.status || "open",
      due_date: job.due_date || "",
      description: job.description || "",
      cost: job.cost == null ? "" : String(job.cost),
      notes: job.notes || "",
      assigned_to: job.assigned_to || "",
      parts: (job.parts_used || []).map((p) => ({
        item_id: p.item_id, item_name: p.item_name, qty: p.qty,
      })),
      before_photos: job.before_photos || [],
      assignee_type: (ext.supplier_name || assignedToProvider) ? "external" : "crew",
      external_supplier_id: ext.supplier_id || "",
      external_supplier_name: ext.supplier_name || "",
    });
    setOpen(true);
  };

  const del = async (id, delInv = false) => {
    try {
      const { data } = await api.delete(`/maintenance/${id}?delete_invoice=${delInv ? "true" : "false"}`);
      if (data?.mode === "approval_requested") toast.success("Deletion request sent to the owner/manager for approval");
      else if (data?.mode === "approval_pending") toast.info("A deletion request is already awaiting approval");
      else toast.success(delInv && data?.invoice_deleted ? "Task and linked invoice deleted" : "Task deleted");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  // Dashboard tile contracts (2026-06-11):
  //   ?status=open      → "Ongoing" pseudo-status: everything that
  //                       isn't ``confirmed`` (signed off) or
  //                       ``cancelled``. ``completed`` (awaiting
  //                       confirmation) is intentionally included
  //                       because work-to-do isn't done until
  //                       confirmed.
  //   ?status=open_only → literal status == "open" — matches the
  //                       "Open tasks" tile that counts only fresh
  //                       work items.
  // External = supplier work: an accepted marketplace supplier, a manually
  // tracked external supplier, or an online provider crew member as assignee.
  const providerMemberIds = new Set(
    members.filter((m) => m.role === "provider").map((m) => m.user_id || m.id),
  );
  const isExternalJob = (i) =>
    !!i.assigned_supplier ||
    !!(i.external_supplier && i.external_supplier.supplier_name) ||
    (!!i.assigned_to && providerMemberIds.has(i.assigned_to));
  const scopeCounts = (() => {
    const base = items.filter((i) => !i.routine_id && !i.deleted_at);
    const external = base.filter(isExternalJob).length;
    return { all: base.length, internal: base.length - external, external };
  })();

  const filtered = items.filter((i) => {
    // Jobs generated from a periodic task live on the Periodic-tasks page
    // (see the "Triggered jobs" section there), not in the Tasks list — keeps
    // Tasks focused on one-off/manual work.
    if (i.routine_id) return false;
    if (scopeFilter === "internal" && isExternalJob(i)) return false;
    if (scopeFilter === "external" && !isExternalJob(i)) return false;
    // Quick "Awaiting quotes" toggle — active quote process, not yet accepted.
    if (awaitingQuotes) {
      const qs = i.quote_summary;
      if (!qs || !qs.total || qs.accepted > 0) return false;
    }
    if (categoryFilter) {
      const cats = (i.system_categories && i.system_categories.length)
        ? i.system_categories
        : (i.system_category ? [i.system_category] : []);
      if (!cats.includes(categoryFilter)) return false;
    }
    if (!statusFilter) return true;
    if (statusFilter === "open") return i.status !== "confirmed" && i.status !== "cancelled";
    if (statusFilter === "open_only") return i.status === "open";
    // Dashboard "Awaiting my confirmation" deep-link → finished-but-not-signed-off
    // work: internal tasks on `done`, vendor jobs on `completed`.
    if (statusFilter === "awaiting_confirmation") return i.status === "done" || i.status === "completed";
    // `done` (internal terminal) is surfaced to users as "Completed" alongside
    // the vendor `completed`, so the single "Completed" filter matches both.
    if (statusFilter === "completed") return i.status === "completed" || i.status === "done";
    // Any other concrete status (dispute, in_progress, …) matches exactly.
    return i.status === statusFilter;
  });

  const categoryOptions = useMemo(() => {
    const set = new Set(SYSTEMS);
    items.forEach((i) => {
      const cats = (i.system_categories && i.system_categories.length)
        ? i.system_categories
        : (i.system_category ? [i.system_category] : []);
      cats.forEach((c) => c && set.add(c));
    });
    return Array.from(set);
  }, [items]);

  if (!activeYacht) return <EmptyYacht />;

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={activeYacht.name}
        title={TITLE}
        right={
          // The "+ Job"/"+ New task" CTA only shows on the Jobs surface — the
          // routine schedule has its own CTA inside RoutineTab. The "?" tour
          // button lives in the page header on the Tasks surface; on the
          // Periodic-tasks surface it's rendered inside RoutineTab (between
          // "Seed templates" and "+ Periodic task").
          <div className="flex items-center gap-2">
            {restrictTo !== "routine" && can("maintenance:create") && (
              <TourReplayButton tourId="create-task" />
            )}
            {restrictTo !== "routine" && can("maintenance:create") && (
              <>
                <input
                  ref={aiDraftRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleAiDraft}
                  data-testid="ai-draft-input"
                />
                <Button
                  variant="outline"
                  className="px-2"
                  onClick={() => aiDraftRef.current?.click()}
                  data-testid="ai-draft-btn"
                  title="Photograph the problem — AI drafts the task title, description, system and priority"
                >
                  <Camera className="h-4 w-4 mr-1.5" />AI Draft
                </Button>
              </>
            )}
          {/* The job editor Dialog. It renders whenever the user can create OR
              edit jobs — the latter so triggered jobs can be edited from the
              Periodic-tasks page (which has no "+ New task" trigger). The
              trigger button itself only shows on the Tasks surface. */}
          {(can("maintenance:create") || can("maintenance:edit")) ? (
          <Dialog open={open} onOpenChange={(o) => {
            if (saving) return;
            if (o) {
              // Opening (via the trigger = new job). Seed the orphan trackers.
              beforeOriginal.current = editingJob ? (editingJob.before_photos || []) : [];
              beforeSession.current = new Set();
            } else {
              // Closing without save (esc/overlay) — discard this session's
              // uploaded-but-unsaved media from storage.
              const orphans = [...beforeSession.current];
              beforeSession.current = new Set();
              beforeOriginal.current = [];
              if (orphans.length) purgeOrphans(orphans);
              setEditingJob(null); setForm(DEFAULT);
            }
            setOpen(o);
          }}>
            {restrictTo !== "routine" && can("maintenance:create") && (
              <DialogTrigger asChild>
                <Button className="bg-ink hover:bg-black" data-testid={ADD_TESTID}><Plus className="h-4 w-4 mr-2" />{ADD_LABEL}</Button>
              </DialogTrigger>
            )}
            <DialogContent
              className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
              onPointerDownOutside={(e) => { if (activeTour) e.preventDefault(); }}
              onInteractOutside={(e) => { if (activeTour) e.preventDefault(); }}
              onEscapeKeyDown={(e) => { if (activeTour) e.preventDefault(); }}
              onOpenAutoFocus={(e) => { if (activeTour) e.preventDefault(); }}
            >
              <TaskEditorBody
                editingJob={editingJob}
                restrictTo={restrictTo}
                dialogTitle={DIALOG_TITLE}
                saveLabel={SAVE_LABEL}
                form={form}
                setForm={setForm}
                items={items}
                inventory={inventory}
                members={members}
                suppliers={suppliers}
                activeYacht={activeYacht}
                beforeSession={beforeSession}
                beforeOriginal={beforeOriginal}
                purgeOrphans={purgeOrphans}
                setEditingJob={setEditingJob}
                setOpen={setOpen}
                setQuotePrompt={setQuotePrompt}
                save={save}
                saving={saving}
              />
            </DialogContent>
          </Dialog>
          ) : null}
          </div>
        }
      />

      {/* Quote send-now-or-later prompt (Find supplier mode) */}
      <AlertDialog open={quotePrompt} onOpenChange={setQuotePrompt}>
        <AlertDialogContent data-testid="quote-prompt">
          <AlertDialogHeader>
            <AlertDialogTitle>Send quote requests?</AlertDialogTitle>
            <AlertDialogDescription>
              You selected {(form._quote_suppliers || []).filter((s) => s.mode !== "online").length} offline supplier(s) for this task.
              <strong> Yes</strong> sends the quote requests (email / in-app invitation) to the offline suppliers.
              <strong> No, will track offline</strong> saves the task with the supplier(s) attached, but they get no
              notifications — your crew handles quotes, statuses and the invoice on their behalf.
              {(form._quote_suppliers || []).some((s) => s.mode === "online") && (
                <> Online suppliers always receive a notification to provide a quote.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="quote-prompt-cancel">Back</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => { setQuotePrompt(false); save({ sendQuotes: true, trackOffline: true }); }}
              data-testid="quote-prompt-offline"
            >No, will track offline</Button>
            <AlertDialogAction
              onClick={() => { setQuotePrompt(false); save({ sendQuotes: true }); }}
              data-testid="quote-prompt-now"
            >Yes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!restrictTo && (
        <div className="mt-6 flex items-center gap-1 border-b border-zinc-200" data-testid="maint-tabs">
          <ModalTab active={view === "jobs"} onClick={() => setView("jobs")} testid="tab-jobs">
            Jobs ({items.length})
          </ModalTab>
          {can("routine:read") && (
            <ModalTab active={view === "routine"} onClick={() => setView("routine")} testid="tab-routine">
              Routine jobs
            </ModalTab>
          )}
        </div>
      )}

      {(restrictTo === "routine" || (!restrictTo && view === "routine")) ? (
        <RoutineTab
          activeYacht={activeYacht}
          members={members}
          inventory={inventory}
          suppliers={suppliers}
          can={can}
          onJobsChanged={() => { load(); loadInventory(); }}
          spawnedJobs={items.filter((i) => i.routine_id && !i.deleted_at)}
          onOpenJob={(j) => { setOpenJob(j); setOpenJobTab("details"); }}
        />
      ) : (
      <>
      <div className="mt-8 inline-flex rounded-lg border border-zinc-200 bg-zinc-100/70 p-0.5" data-testid="maint-scope-tabs">
        {[["all", "All"], ["internal", "Internal"], ["external", "External"]].map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => pickScope(v)}
            aria-pressed={scopeFilter === v}
            className={`h-8 px-3.5 rounded-md text-sm transition-colors ${scopeFilter === v ? "bg-white shadow-sm font-medium text-zinc-900" : "text-zinc-500 hover:text-zinc-800"}`}
            data-testid={`maint-scope-${v}`}
          >
            {label}
            <span className={`ml-1.5 text-[11px] ${scopeFilter === v ? "text-zinc-400" : "text-zinc-400/80"}`}>{scopeCounts[v]}</span>
          </button>
        ))}
      </div>
      <div className="mt-3 flex gap-2 flex-wrap">
        <select
          className="h-9 rounded-md border border-input px-3 text-sm w-full sm:w-auto"
          // Show empty value while the "open"/"open_only" pseudo-filters are
          // active so the dropdown doesn't display a phantom option.
          value={(statusFilter === "open" || statusFilter === "open_only" || statusFilter === "awaiting_confirmation") ? "" : statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="maint-status-filter"
        >
          <option value="">{t("tasks.all_statuses")}</option>
          {STATUS.filter((s) => s !== "done").map((s) => <option key={s} value={s}>{t(`tasks.status_${s}`)}</option>)}
        </select>
        <select
          className="h-9 rounded-md border border-input px-3 text-sm w-full sm:w-auto"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          data-testid="maint-category-filter"
        >
          <option value="">All categories</option>
          {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setAwaitingQuotes((v) => !v)}
          aria-pressed={awaitingQuotes}
          className={`inline-flex items-center gap-1.5 h-9 rounded-full border px-3 text-xs transition-colors ${awaitingQuotes ? "border-amber-300 bg-amber-100 text-amber-900" : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"}`}
          data-testid="maint-awaiting-quotes-toggle"
        >
          <Clock className="h-3.5 w-3.5" />Awaiting quotes
          {awaitingQuotes && <span aria-hidden className="text-amber-500">×</span>}
        </button>
        {(statusFilter === "open" || statusFilter === "open_only") && (
          <button
            type="button"
            onClick={() => setStatusFilter("")}
            className="inline-flex items-center gap-1.5 h-9 rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-3 text-xs"
            data-testid="maint-open-chip"
          >
            {statusFilter === "open_only" ? t("tasks.status_open") : t("tasks.open_jobs_only")}
            <span aria-hidden className="text-amber-500">×</span>
          </button>
        )}
        {statusFilter === "awaiting_confirmation" && (
          <button
            type="button"
            onClick={() => setStatusFilter("")}
            className="inline-flex items-center gap-1.5 h-9 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 text-xs"
            data-testid="maint-awaiting-confirm-chip"
          >
            {t("tasks.awaiting_confirmation")}
            <span aria-hidden className="text-emerald-500">×</span>
          </button>
        )}
      </div>

      <div className="mt-4">
        {(() => {
          // Active vs deleted partition — deleted rows render under a
          // single "Deleted" group at the very bottom (strikethrough),
          // regardless of the active grouping mode.
          const activeItems = filtered.filter((i) => !i.deleted_at);
          const deletedItems = filtered.filter((i) => i.deleted_at);

          // Group by status when on the Tasks page (`restrictTo === "jobs"`)
          // AND no specific status filter is active. Filtered/"open" views
          // stay as a single flat list so the user can scan one bucket
          // without the group chrome getting in the way.
          const groupByStatus = restrictTo === "jobs" && (!statusFilter || statusFilter === "open");
          const groups = groupByStatus
            ? STATUS_GROUP_ORDER.filter((s) => s !== "done").map((s) => ({
                status: s,
                // Internal `done` tasks fold into the "Completed" group so the
                // user sees a single Completed section (label + colour match).
                items: activeItems.filter((it) => it.status === s || (s === "completed" && it.status === "done")),
              })).filter((g) => g.items.length > 0)
            : [{ status: null, items: activeItems }];
          if (deletedItems.length > 0) {
            groups.push({ status: "deleted", items: deletedItems, isDeleted: true });
          }

          if (filtered.length === 0) {
            return (
              <div className="rounded-md border border-zinc-200 bg-white p-10 text-center text-zinc-500" data-testid="maint-empty">
                <Wrench className="h-6 w-6 mx-auto text-zinc-300" />
                <div className="mt-2">{t("tasks.no_tasks")}</div>
              </div>
            );
          }

          return groups.map((g) => (
            <TaskGroup
              key={g.status || "all"}
              group={g}
              members={members}
              onOpen={(job, tab) => { setOpenJob(job); setOpenJobTab(tab); }}
              onRefresh={load}
            />
          ));
        })()}
      </div>
      </>
      )}

      {openJob && (
        <JobDetailModal
          job={openJob}
          inventory={inventory}
          members={members}
          userId={user?.id}
          userRole={meRole}
          can={can}
          initialTab={openJobTab}
          deleteLabel={DELETE_LABEL}
          onClose={() => setOpenJob(null)}
          onRefresh={async () => { await load(); }}
          onEdit={async () => {
            // Re-fetch the latest snapshot so the editor opens with
            // the same data the modal showed (avoids stale state when
            // the user has just changed status from the dropdown).
            const job = openJob;
            setOpenJob(null);
            startEditJob(job);
          }}
          onDelete={async (delInv) => {
            const id = openJob.id;
            setOpenJob(null);
            await del(id, delInv);
          }}
        />
      )}
    </div>
  );
}
